import { format, isToday, isTomorrow, isAfter, startOfDay } from 'date-fns';

// Generate a random confirmation code
export const generateConfirmationCode = () => {
  return Math.random().toString(36).substr(2, 9).toUpperCase();
};

// Generate a random management token
export const generateManagementToken = () => {
  return Math.random().toString(36).substr(2, 15);
};

// Format date for display
export const formatDate = (date) => {
  if (!date) return '';
  
  const dateObj = new Date(date);
  
  if (isToday(dateObj)) {
    return 'Today';
  }
  
  if (isTomorrow(dateObj)) {
    return 'Tomorrow';
  }
  
  return format(dateObj, 'MMM dd, yyyy');
};

// Format time for display
export const formatTime = (time) => {
  if (!time) return '';
  
  // Handle both HH:mm and HH:mm:ss formats
  const [hours, minutes] = time.split(':');
  const date = new Date();
  date.setHours(parseInt(hours), parseInt(minutes), 0);
  
  return format(date, 'h:mm a');
};

// Check if a date is available for booking
export const isDateAvailable = (date, blockedDates = [], existingAppointments = []) => {
  const dateStr = format(date, 'yyyy-MM-dd');
  
  // Check if date is in the past
  if (!isAfter(date, startOfDay(new Date()))) {
    return false;
  }
  
  // Check if date is blocked
  const isBlocked = blockedDates.some(blocked => 
    blocked.blocked_date === dateStr
  );
  
  if (isBlocked) {
    return false;
  }
  
  return true;
};

// Generate time slots based on availability settings
export const generateTimeSlots = (availabilitySettings, selectedDate, existingAppointments = []) => {
  const dayOfWeek = selectedDate.getDay(); // 0 = Sunday, 1 = Monday, etc.
  
  // Find availability for the selected day
  const dayAvailability = availabilitySettings.find(setting => 
    setting.day_of_week === dayOfWeek && setting.is_available === 1
  );
  
  if (!dayAvailability) {
    return [];
  }
  
  const slots = [];
  const startTime = dayAvailability.start_time;
  const endTime = dayAvailability.end_time;
  
  // Generate 30-minute slots
  const [startHour, startMinute] = startTime.split(':').map(Number);
  const [endHour, endMinute] = endTime.split(':').map(Number);
  
  let currentHour = startHour;
  let currentMinute = startMinute;
  
  while (currentHour < endHour || (currentHour === endHour && currentMinute < endMinute)) {
    const timeStr = `${currentHour.toString().padStart(2, '0')}:${currentMinute.toString().padStart(2, '0')}`;
    
    // Check if this time slot is already booked
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    const isBooked = existingAppointments.some(appointment => 
      appointment.appointment_date === dateStr && 
      appointment.appointment_time === timeStr &&
      appointment.status !== 'cancelled'
    );
    
    if (!isBooked) {
      slots.push(timeStr);
    }
    
    // Add 30 minutes
    currentMinute += 30;
    if (currentMinute >= 60) {
      currentMinute = 0;
      currentHour++;
    }
  }
  
  return slots;
};

// Validate email format
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Validate phone number format
export const isValidPhone = (phone) => {
  const phoneRegex = /^[+]?[1-9]\d{0,15}$/;
  return phoneRegex.test(phone.replace(/[\s()-]/g, ''));
};

// Format phone number for display
export const formatPhoneNumber = (phone) => {
  const cleaned = phone.replace(/\D/g, '');
  
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  
  if (cleaned.length === 11 && cleaned[0] === '1') {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  
  return phone;
};